import { APP_BASE_URL } from "../services/api";

/**
 * Strip HTML tags and collapse whitespace for use in meta descriptions.
 */
export function stripHtml(html) {
  if (!html) return "";
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Ensure image URLs are absolute. IMAGE_BASE_URL is "" in prod so we
 * fall back to APP_BASE_URL so OG image tags have a full URL.
 */
export function buildAbsoluteImageUrl(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${APP_BASE_URL}${path}`;
}

/**
 * Truncate a string to maxLen characters, appending "…" when trimmed.
 */
function truncate(str, maxLen = 160) {
  if (!str || str.length <= maxLen) return str || "";
  return str.slice(0, maxLen - 1) + "…";
}

/**
 * Extract the first plain-text paragraph from an articleContent array
 * (the CKEditor block format used by several pages).
 */
function firstArticleText(articleContent) {
  if (!Array.isArray(articleContent)) return "";
  for (const block of articleContent) {
    if (block && block.text) {
      const text = stripHtml(block.text);
      if (text.length > 20) return text;
    }
  }
  return "";
}

/**
 * Build a { title, description, image } meta object from a CMS record.
 * `type` is one of: "program" | "possibility" | "triangle" | "project" |
 *                   "artist" | "employee"
 */
export function metaFromRecord(record, type) {
  if (!record) return {};

  switch (type) {
    case "program": {
      const title = record.title || "";
      const description =
        truncate(firstArticleText(record.articleContent)) || truncate(title);
      const image = record.mainImg
        ? buildAbsoluteImageUrl(record.mainImg.url)
        : "";
      return { title, description, image };
    }

    case "possibility": {
      const title = record.title || "";
      const description =
        truncate(stripHtml(record.aboutProject)) || truncate(title);
      const image = record.imageOne
        ? buildAbsoluteImageUrl(record.imageOne.url)
        : "";
      return { title, description, image };
    }

    case "triangle": {
      const title = record.title || "";
      const description =
        truncate(firstArticleText(record.articleContent)) || truncate(title);
      const image = record.mainImg
        ? buildAbsoluteImageUrl(record.mainImg.url)
        : "";
      return { title, description, image };
    }

    case "project": {
      const title = record.title || "";
      const description =
        truncate(firstArticleText(record.articleContent)) || truncate(title);
      const image = record.image1
        ? buildAbsoluteImageUrl(record.image1.url)
        : "";
      return { title, description, image };
    }

    case "artist": {
      const title = record.name || "";
      const description = truncate(stripHtml(record.bio || record.intro)) || truncate(title);
      const image = record.profileImg
        ? buildAbsoluteImageUrl(record.profileImg.url)
        : "";
      return { title, description, image };
    }

    case "employee": {
      const title = record.name || "";
      const description = truncate(stripHtml(record.bio || record.intro)) || truncate(title);
      const image = record.profileImg
        ? buildAbsoluteImageUrl(record.profileImg.url)
        : "";
      return { title, description, image };
    }

    default:
      return { title: record.title || record.name || "", description: "", image: "" };
  }
}
